import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Chip
} from '@mui/material';
import {
  NoteAdd as NoteAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Note as NoteIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [openNoteDialog, setOpenNoteDialog] = useState(false);
  const [currentNote, setCurrentNote] = useState({ 
    id: null,
    title: '', 
    content: '',
    priority: 'normal'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [companyConfig, setCompanyConfig] = useState(null);

  // Fetch current user to get employee_id
  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/current`, {
        withCredentials: true
      });

      if (!response.data || !response.data.employee_id) {
        throw new Error('Employee ID not found in user data');
      }

      setEmployeeId(response.data.employee_id);
      return response.data.employee_id;
    } catch (err) {
      //console.error("Error fetching current user:", err);
      setError('Failed to fetch user information');
      return null;
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      const userId = await fetchCurrentUser();
      if (userId) {
        await fetchNotes(userId);
      }
      
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.company_id) {
            const res = await axios.get(`${API_URL}/api/cmp/${payload.company_id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.data && res.data.data) {
              setCompanyConfig(res.data.data);
            }
          }
        } catch (e) {
          // console.error("Error fetching company config", e);
        }
      }
    };
    initializeData();
  }, []);

  const fetchNotes = async (userId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        //console.error("Token is missing");
        setError('Authentication token is missing');
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
  
      const response = await axios.get(`${API_URL}/api/notes`, {
        params: { 
          createdBy: userId,
          company_id: company_id
        },
        headers: { Authorization: `Bearer ${token}` },
      });
  
      // Backend returns { notes: [...] } so extract the notes array
      setNotes(response.data.notes || response.data);
    } catch (err) {
      //console.error("Error fetching notes:", err);
      setError('Failed to fetch notes');
    } finally {
      setLoading(false);
    }
  };

  const saveNote = async () => {
    if (!currentNote.title || !currentNote.content) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      if (isEditing && currentNote.id) {
        // Update existing note
        const response = await axios.put(
          `${API_URL}/api/notes/${currentNote.id}?createdBy=${employeeId}`,
          {
            title: currentNote.title,
            content: currentNote.content,
            priority: currentNote.priority
          },{
            headers: { Authorization: `Bearer ${token}`,
         
                "Content-Type": "application/json"
           }
          }
        );

        setNotes(prev => prev.map(note => 
          note.id === currentNote.id ? response.data.note : note
        ));
      } else {
        // Create new note - add createdBy in body
        const response = await axios.post(
          `${API_URL}/api/notes`,
          {
            title: currentNote.title,
            content: currentNote.content,
            priority: currentNote.priority,
            createdBy: employeeId
          },
          {
            headers: { Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"}
          }
        );

        setNotes(prev => [response.data.note, ...prev]);
      }

      closeDialog();
    } catch (error) {
      //console.error('Error saving note:', error);
      setError(error.response?.data?.message || 'Failed to save note');
    } finally {
      setLoading(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      // Add createdBy parameter to ensure user can only delete their own notes
      await axios.delete(
        `${API_URL}/api/notes/${noteId}?createdBy=${employeeId}`,
        {
          headers: { Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"  }
        }
      );

      setNotes(prev => prev.filter(note => note.id !== noteId));
      handleMenuClose();
    } catch (error) {
      //console.error('Error deleting note:', error);
      setError('Failed to delete note');
    }
  };

  const handleMenuOpen = (event, noteId) => {
    setAnchorEl(event.currentTarget);
    setSelectedNoteId(noteId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedNoteId(null);
  };

  const handleEdit = () => {
    const noteToEdit = notes.find(note => note.id === selectedNoteId);
    if (noteToEdit) {
      setCurrentNote({
        id: noteToEdit.id,
        title: noteToEdit.title,
        content: noteToEdit.content,
        priority: noteToEdit.priority || 'normal'
      });
      setIsEditing(true);
      setOpenNoteDialog(true);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      deleteNote(selectedNoteId);
    }
  };

  const closeDialog = () => {
    setOpenNoteDialog(false);
    setCurrentNote({ id: null, title: '', content: '', priority: 'normal' });
    setIsEditing(false);
    setError('');
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    if (companyConfig?.timezone) {
      options.timeZone = companyConfig.timezone;
    }
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <Card sx={{ 
      height: 450, 
      display: 'flex', 
      flexDirection: 'column', 
      border: '1px solid', 
      borderColor: 'grey.300', 
      boxShadow: 2,
      overflow: 'hidden' // Prevent card from overflowing
    }}>
      {/* Sticky Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        px: 3, 
        py: 2,
        borderBottom: '1px solid #eee',
        bgcolor: 'background.paper',
        flexShrink: 0 // Prevent header from shrinking
      }}>
        <Typography variant="h6" fontWeight="bold" component="h2" sx={{ display: 'flex', alignItems: 'center' }}>
          <NoteIcon sx={{ mr: 1, color: '#ff9800', fontSize: 32 }} />
          Notes
        </Typography>
        <IconButton
          onClick={() => {
            setIsEditing(false);
            setOpenNoteDialog(true);
          }}
          disabled={loading}
          sx={{ bgcolor: '#ff9800', color: 'white', '&:hover': { bgcolor: '#f57c00' } }}
        >
          <NoteAddIcon />
        </IconButton>
      </Box>

      {/* Scrollable Content Area */}
      <Box sx={{ 
        flexGrow: 1, 
        overflowY: 'auto',
        px: 3,
        py: 1,
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#ffb74d',
          borderRadius: '4px',
          '&:hover': {
            background: '#ff9800',
          },
        },
        // Firefox scrollbar styling
        scrollbarWidth: 'thin',
        scrollbarColor: '#ffb74d #f1f1f1',
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading && notes.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>Loading notes...</Typography>
          </Box>
        ) : notes.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <NoteIcon sx={{ fontSize: 48, color: 'grey.300', mb: 2 }} />
            <Typography variant="body2" color="text.secondary">No notes yet</Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notes.map((note) => (
              <ListItem
                key={note.id}
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: 'background.paper',
                  display: 'block',
                  p: 2,
                  position: 'relative',
                  '&:hover': {
                    borderColor: '#ff9800',
                    boxShadow: '0 2px 8px rgba(255, 152, 0, 0.1)',
                  }
                }}
              >
                <Box sx={{ pr: 4 }}> {/* Add padding right for the menu button */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {note.title}
                    </Typography>
                    {note.priority && note.priority !== 'normal' && (
                      <Chip 
                        label={note.priority} 
                        size="small" 
                        color={getPriorityColor(note.priority)}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      mb: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.5
                    }}
                  >
                    {note.content}
                  </Typography>
                  
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(note.createdAt)}
                  </Typography>
                </Box>

                {/* Menu Button positioned absolutely */}
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, note.id)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    color: 'grey.500',
                    '&:hover': {
                      color: '#ff9800',
                      backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    }
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            borderRadius: 2,
          }
        }}
      >
        <MenuItem onClick={handleEdit} sx={{ py: 1 }}>
          <EditIcon sx={{ mr: 1.5, fontSize: 18, color: '#ff9800' }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ py: 1 }}>
          <DeleteIcon sx={{ mr: 1.5, fontSize: 18, color: '#f44336' }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Add/Edit Note Dialog */}
      <Dialog 
        open={openNoteDialog} 
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 2 }}>
          <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
            <NoteAddIcon sx={{ mr: 1, color: '#ff9800' }} />
            {isEditing ? 'Edit Note' : 'Add New Note'}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5,mt:4 }}>
            <TextField
              label="Title"
              required
              fullWidth
              value={currentNote.title}
              onChange={(e) => setCurrentNote({...currentNote, title: e.target.value})}
              placeholder="Enter note title"
              variant="outlined"
              size="small"
            />

            <TextField
              label="Content"
              required
              fullWidth
              multiline
              rows={6}
              value={currentNote.content}
              onChange={(e) => setCurrentNote({...currentNote, content: e.target.value})}
              placeholder="Enter note content"
              variant="outlined"
            />

            <TextField
              label="Priority"
              select
              fullWidth
              value={currentNote.priority}
              onChange={(e) => setCurrentNote({...currentNote, priority: e.target.value})}
              variant="outlined"
              size="small"
            >
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </TextField>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, borderTop: '1px solid #eee' }}>
          <Button onClick={closeDialog} color="inherit" sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button
            onClick={saveNote}
            variant="contained"
            disabled={!currentNote.title || !currentNote.content || loading}
            startIcon={loading ? <CircularProgress size={16} /> : <NoteAddIcon />}
            sx={{
              bgcolor: '#ff9800',
              '&:hover': {
                bgcolor: '#f57c00',
              },
              '&:disabled': {
                bgcolor: '#ffb74d',
              }
            }}
          >
            {loading ? 'Saving...' : isEditing ? 'Update Note' : 'Add Note'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}